export const integrationArticles = [
  {
    slug: 'on-device-data-stream',
    title: 'On-device data stream',
    summary: 'Build a local subscriber for low-latency detection events produced by an AdMobilize Vision Device.',
    category: 'integrations',
    sources: [
      { file: 'admobilize-on-device-integration-685-2.pdf' },
      { file: 'admobilize-on-device-integration-685.pdf' },
      { file: 'admobilize-on-device-integration (2).pdf' },
      { file: 'admobilize-on-device-integration.pdf' }
    ],
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        paragraphs: [
          'The on-Device stream is intended for applications that need a local event soon after Vision produces it, such as a content trigger or an on-premise processing workflow. AdMobilize Vision publishes those events through a ZeroMQ publisher-subscriber stream, and the subscriber decodes each message with the AdMobilize Protocol Buffer definitions for the deployed solution.',
          'Keep the subscriber callback short. Validate and enqueue the decoded event there, then perform slower storage, network, or media work elsewhere so the application does not block the stream.'
        ],
        bullets: [
          'Use the maintained AdMobilize Protocol Buffer package for your language.',
          'Match the decoder and message type to the Device solution.',
          'Treat image bytes and detection metadata as sensitive operational data.'
        ]
      },
      {
        id: 'real-time-events',
        title: 'Real-time events for triggering',
        paragraphs: [
          'The standard Audience stream emits a completed event after the tracked subject leaves the zone or can no longer be detected. That timing supports counting and duration measurements, but it may be later than a content-triggering application expects.',
          'A workflow that needs to react when someone enters can use entry events. Open the Device settings and enable the available entry-person event option before building a dependency on those messages.'
        ]
      },
      {
        id: 'expose-stream',
        title: 'Expose the local stream',
        paragraphs: [
          'Container-based and BalenaOS deployments may require an additional exposure step before an application on the host can reach the local publisher. The Linux and BalenaOS installation guides provide context for those deployment models.',
          'Use the command documented for the installed admclient release because the command and channel mapping can change with the deployment package.',
          'AdMobilize-managed Devices may restrict local integrations. Contact support before designing an integration around a managed Device.'
        ],
        paragraphLinks: [
          { label: 'Linux', slug: 'linux-on-device', section: 'install' },
          { label: 'BalenaOS installation', slug: 'balena-sensor-setup', section: 'requirements' }
        ],
        steps: [
          'Confirm that the Device software and Protocol Buffer package are compatible.',
          'Expose the local stream with the supported admclient workflow for the deployment.',
          'Confirm the event and status channels assigned to the installed solution.',
          'Connect a subscriber and verify that it can decode a test event.'
        ]
      },
      {
        id: 'channels',
        title: 'Event channels',
        paragraphs: [
          'Message types and channels vary by Device configuration, including between Traffic deployments. Obtain the current assignments from the installed Device configuration or AdMobilize support instead of hard-coding values from another deployment.'
        ],
        tables: [
          {
            caption: 'Local stream roles',
            headers: ['Channel role', 'Direction', 'Content'],
            rows: [
              ['Detection events', 'Device to subscriber', 'Completed Audience or Traffic results'],
              ['Configuration', 'Integration to Device', 'Supported commands for the Vision process'],
              ['Status', 'Device to subscriber', 'Command responses and frame information'],
              ['JSON results', 'Device to subscriber', 'JSON-formatted results when the deployment enables this stream']
            ]
          }
        ]
      },
      {
        id: 'json-results',
        title: 'JSON results',
        paragraphs: [
          'Some deployments can publish a JSON result stream in addition to Protocol Buffer messages. AdMobilize support may need to enable and expose that stream on BalenaOS Devices.',
          'Confirm both the JSON schema and listener channel for the installed release before implementing a consumer. Treat a sample from another runtime as illustrative, not as the current contract.'
        ]
      },
      {
        id: 'subscriber-examples',
        title: 'Subscriber setup',
        paragraphs: [
          'Node.js and Python subscribers follow the same basic sequence: install the ZeroMQ bindings and maintained AdMobilize Protocol Buffer package for the language, connect to the assigned event channel, decode the solution message, and hand it to the rest of the application without blocking reception.'
        ],
        steps: [
          'Create a ZeroMQ subscriber socket.',
          'Connect to tcp://DEVICE_HOST:EVENT_PORT.',
          'Subscribe without a topic filter unless the deployment defines one.',
          'Decode each buffer as the message type for the selected solution.',
          'Pass the decoded result to a non-blocking application queue.'
        ],
        code: 'Stream: tcp://DEVICE_HOST:EVENT_PORT\nDecoder: YOUR_SOLUTION_MESSAGE_TYPE\nHandler: decode -> validate -> enqueue'
      },
      {
        id: 'event-output',
        title: 'Event output',
        paragraphs: [
          'A decoded event contains the fields supported by its solution, model, and runtime. Validate optional demographic, image, and tracking fields before using them; a field present in one deployment may be absent in another.'
        ],
        tables: [
          {
            caption: 'Common event content',
            headers: ['Event', 'Typical content'],
            rows: [
              ['Face', 'Age, emotion, session time, dwell time, tracking reference, and optional result image'],
              ['Person', 'Gender, session time, dwell time, and tracking reference'],
              ['Vehicle', 'Vehicle type, confidence, location, session time, and tracking reference']
            ]
          }
        ]
      },
      {
        id: 'frame-data',
        title: 'Getting frame data',
        paragraphs: [
          'Frame data reports the detections present in the current image as they are requested. The integration sends a frame-information request on the configuration channel and reads the matching response from the status channel.',
          'The response can contain detection rectangles, confidence values, types, tracking references, and session information. Poll only as often as the application requires, and confirm that the installed schema supports the frame-information command before relying on it.'
        ],
        steps: [
          'Open a push socket on the assigned configuration channel.',
          'Open a subscriber socket on the assigned status channel.',
          'Encode and send the supported frame-information request.',
          'Filter status messages for the matching frame-information response.',
          'Decode the response and process its detection collection.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Devices', to: '/devices' }],
    related: ['linux-on-device', 'device-status-api'],
    legacy: false
  },
  {
    slug: 'authentication-v2',
    title: 'Authentication v2',
    summary: 'Maintain an existing integration that signs in for an access token and renews it with a refresh token.',
    category: 'developer',
    sources: [{ file: 'authentication-v2.pdf' }],
    sections: [
      {
        id: 'overview',
        title: 'Authentication overview',
        paragraphs: [
          'Authentication v2 is a legacy sign-in flow used by existing integrations, including clients of the Data API and Device Status API. The application signs in with credentials registered with AdMobilize, receives a token pair, and sends the access token with later requests.',
          'Send credentials and tokens only over HTTPS and keep them in an approved secret store. The service returns an access token, a refresh token, and an expiration interval; read that interval from every successful response instead of assuming a fixed lifetime.'
        ],
        paragraphLinks: [
          { label: 'Data API', slug: 'data-api', section: 'overview' },
          { label: 'Device Status API', slug: 'device-status-api', section: 'prerequisites' }
        ]
      },
      {
        id: 'login',
        title: 'Get an access token from credentials',
        paragraphs: [
          'Send the credentials registered with AdMobilize to the login endpoint. A successful response provides the access token used as a Bearer credential, the refresh token used for renewal, and the interval until expiry.'
        ],
        tables: [
          {
            caption: 'Login request',
            headers: ['Item', 'Value'],
            rows: [
              ['Method', 'POST'],
              ['Endpoint', 'https://xauth.admobilize.com/api/v1/users:login'],
              ['Content-Type', 'application/json']
            ]
          }
        ],
        code: '{\n  "email": "YOUR_EMAIL",\n  "password": "YOUR_PASSWORD"\n}\n\n{\n  "accessToken": "YOUR_ACCESS_TOKEN",\n  "refreshToken": "YOUR_REFRESH_TOKEN",\n  "expiresIn": "EXPIRATION_INTERVAL"\n}'
      },
      {
        id: 'use-token',
        title: 'Use the access token',
        paragraphs: [
          'Send the access token in the Authorization header for each protected request. Keep it out of URLs, logs, screenshots, analytics events, and source control.'
        ],
        code: 'Authorization: Bearer YOUR_ACCESS_TOKEN'
      },
      {
        id: 'refresh',
        title: 'Refresh access',
        paragraphs: [
          'Exchange the current refresh token for a new token pair before the application loses access. After a successful refresh, replace both stored tokens with the returned values so the integration does not try to reuse an older refresh token.'
        ],
        tables: [
          {
            caption: 'Refresh request',
            headers: ['Item', 'Value'],
            rows: [
              ['Method', 'POST'],
              ['Endpoint', 'https://xauth.admobilize.com/api/v1/users:refreshToken'],
              ['Content-Type', 'application/json']
            ]
          }
        ],
        code: '{\n  "refreshToken": "YOUR_REFRESH_TOKEN"\n}\n\n{\n  "accessToken": "YOUR_NEW_ACCESS_TOKEN",\n  "refreshToken": "YOUR_NEW_REFRESH_TOKEN",\n  "expiresIn": "EXPIRATION_INTERVAL"\n}'
      },
      {
        id: 'authentication-errors',
        title: 'Handle authentication errors',
        paragraphs: [
          'Handle authentication failures as explicit application states. A bad credential, an expired refresh token, and a rate-limited request require different responses; repeated blind retries can make recovery slower and hide the real problem.'
        ],
        bullets: [
          'Reject the request when the login response does not contain both tokens.',
          'Prompt for new credentials after an invalid-credential response.',
          'Stop retrying when the service rate-limits the client and honor the response guidance.',
          'Require a new sign-in when the refresh token is invalid or expired.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Profile', to: '/profile' }],
    related: ['managing-api-keys', 'data-api', 'device-status-api', 'authentication-v1-legacy'],
    legacy: true,
    legacyNotice: {
      title: 'Legacy',
      paragraphs: ['This guide describes the access-token flow used by existing integrations.'],
      replacement: {
        before: 'For new public API integrations, create and manage ',
        after: ' in the Dashboard.',
        label: 'API keys',
        slug: 'managing-api-keys'
      }
    }
  },
  {
    slug: 'data-api',
    title: 'Data API',
    summary: 'Create an asynchronous job for bulk analytics data, monitor it responsibly, and retrieve complete results or export files.',
    category: 'developer',
    sources: [{ file: 'data-api.pdf' }],
    sections: [
      {
        id: 'overview',
        title: 'Data API overview',
        paragraphs: [
          'Use the Data API when an integration needs a bulk dataset that is too large or expensive to prepare in a single request. It creates an asynchronous job for detection data; it is not a real-time event stream.',
          'The client creates a job, saves its identifier, checks the job state, and then retrieves paginated JSON results or export files. Leave at least five minutes between status requests so the client does not create unnecessary load while the job is running.'
        ]
      },
      {
        id: 'prerequisites',
        title: 'Prerequisites',
        paragraphs: [
          'Decide the scope and time range before creating a job. Obtain the access token through Authentication v2, identify the Site Group and optional Devices, and use explicit UTC offsets so the request does not depend on a local clock assumption.'
        ],
        paragraphLinks: [{ label: 'Authentication v2', slug: 'authentication-v2', section: 'login' }],
        bullets: [
          'An Authentication v2 access token.',
          'The Site Group and optional Device scope for the query.',
          'A start and end time with explicit UTC offsets.',
          'A supported product and any required CMS settings.'
        ]
      },
      {
        id: 'create-job',
        title: 'Step 1: Create a job',
        paragraphs: [
          'Create the job with the required time range and product. Omit deviceIds when the request should include every Device available within the selected scope. If projectId is absent, the service selects the account’s default Site Group.',
          'A load-time range takes precedence over the event-time range when both are present. Use it only when the integration intentionally needs ingestion time instead of the time an event occurred.'
        ],
        tables: [
          {
            caption: 'Create-job request',
            headers: ['Item', 'Value'],
            rows: [
              ['Method', 'POST'],
              ['Endpoint', 'https://api.admobilize.com/v1alpha1/jobs'],
              ['Authorization', 'Bearer access token'],
              ['Content-Type', 'application/json']
            ]
          },
          {
            caption: 'Create-job fields',
            headers: ['Field', 'Required', 'Purpose'],
            rows: [
              ['startTime', 'Yes', 'Start of the event-time range with UTC offset'],
              ['endTime', 'Yes', 'End of the event-time range with UTC offset'],
              ['productId', 'Yes', 'audience or traffic data source'],
              ['projectId', 'No', 'Site Group scope; defaults to the account Site Group'],
              ['deviceIds', 'No', 'Device filter; omit to include the full Site Group scope'],
              ['cms', 'No', 'CMS data source when the product supports it'],
              ['timezone', 'No', 'IANA timezone used to convert result timestamps'],
              ['speedUnit', 'No', 'km or miles for Traffic speed conversion'],
              ['loadTime', 'No', 'Start of ingestion-time range'],
              ['loadTimeEnd', 'No', 'End of ingestion-time range'],
              ['summarizedFields', 'No', 'Requested aggregation groups']
            ]
          }
        ],
        code: '{\n  "startTime": "START_TIME_WITH_OFFSET",\n  "endTime": "END_TIME_WITH_OFFSET",\n  "productId": "audience",\n  "projectId": "YOUR_SITE_GROUP_ID",\n  "deviceIds": ["YOUR_DEVICE_ID"],\n  "timezone": "YOUR_IANA_TIMEZONE",\n  "summarizedFields": [\n    { "aggregation": "COUNT", "fields": ["impressions"] }\n  ]\n}'
      },
      {
        id: 'create-job-response',
        title: 'Create-job response',
        paragraphs: [
          'Save jobId as soon as the request succeeds; every later operation uses it. The API contract defines PENDING, RUNNING, DONE, and ERROR. The current service can report request or processing failures as API errors and may leave detail and statusMessage empty, so do not depend on those fields for failure handling.'
        ],
        code: '{\n  "jobId": "YOUR_JOB_ID",\n  "status": "RUNNING",\n  "detail": "",\n  "statusMessage": "",\n  "selfLink": "JOB_STATUS_URL"\n}'
      },
      {
        id: 'check-status',
        title: 'Step 2: Check job status',
        paragraphs: [
          'Request the saved job until it reaches DONE, leaving at least five minutes between status checks. Stop polling when the API returns an error and handle that response before deciding whether a corrected request should be submitted.'
        ],
        tables: [
          {
            caption: 'Job status request',
            headers: ['Item', 'Value'],
            rows: [
              ['Method', 'GET'],
              ['Endpoint', 'https://api.admobilize.com/v1alpha1/jobs/YOUR_JOB_ID'],
              ['Authorization', 'Bearer access token']
            ]
          },
          {
            caption: 'Job states',
            headers: ['State', 'Meaning'],
            rows: [
              ['PENDING', 'The service accepted the job and has not started it.'],
              ['RUNNING', 'The service is processing the job.'],
              ['DONE', 'Results are ready.'],
              ['ERROR', 'Defined by the API contract; current failures can instead be returned as API errors.']
            ]
          }
        ]
      },
      {
        id: 'retrieve-results',
        title: 'Step 3: Retrieve results',
        paragraphs: [
          'A completed job supports paginated results and JSON, CSV, or Avro file exports. The datapoints field is itself a JSON-encoded string, so parse it before reading the result array.',
          'When reading raw results, follow nextPageToken until it is empty. A successful first page is not evidence that the dataset is complete.'
        ],
        tables: [
          {
            caption: 'Result operations',
            headers: ['Operation', 'Method and endpoint', 'Output'],
            rows: [
              ['Raw results', 'GET /v1alpha1/jobs/YOUR_JOB_ID/results?pageSize=PAGE_SIZE&pageToken=PAGE_TOKEN', 'datapoints and nextPageToken'],
              ['JSON export', 'GET /v1alpha1/jobs/YOUR_JOB_ID/export?format=json', 'One or more temporary download URLs'],
              ['CSV export', 'GET /v1alpha1/jobs/YOUR_JOB_ID/export?format=csv', 'One or more temporary download URLs'],
              ['Avro export', 'GET /v1alpha1/jobs/YOUR_JOB_ID/export?format=avro', 'One or more temporary download URLs']
            ]
          }
        ],
        code: '{\n  "jobId": "YOUR_JOB_ID",\n  "jobComplete": true,\n  "datapoints": "JSON_ENCODED_ARRAY",\n  "nextPageToken": "NEXT_PAGE_TOKEN"\n}'
      },
      {
        id: 'sample-data',
        title: 'Sample data schemas',
        paragraphs: [
          'Returned fields depend on the selected product, Device configuration, and CMS integration. Treat optional fields as unavailable when they are absent instead of filling them with assumed values.'
        ],
        tables: [
          {
            caption: 'Audience fields',
            headers: ['Field group', 'Fields'],
            rows: [
              ['References', 'deviceId, cameraId, detectionId, zoneId'],
              ['Classification', 'type, emotion, gender, age, direction, mask'],
              ['Measurements', 'count, isView, estimatedImpression, dwellTime, sessionTime'],
              ['Context', 'deviceName, timestamp']
            ]
          },
          {
            caption: 'Traffic fields',
            headers: ['Field group', 'Fields'],
            rows: [
              ['References', 'deviceId, zoneId'],
              ['Measurements', 'timestamp, count, direction, and available event, speed, or vehicleType fields'],
              ['Context', 'deviceName']
            ]
          }
        ]
      },
      {
        id: 'errors',
        title: 'Common errors',
        paragraphs: [
          'Use the request stage and API response to narrow the problem before retrying. Authentication, scope, product selection, and processing failures each call for a different correction.'
        ],
        tables: [
          {
            caption: 'Data API errors',
            headers: ['Error', 'Action'],
            rows: [
              ['Expired or malformed token', 'Refresh the access token and remove whitespace from the Authorization header.'],
              ['Missing credentials', 'Send a valid Bearer access token.'],
              ['Method does not exist', 'Check the method and jobs path.'],
              ['Dataset or table not found', 'Check productId, cms, Site Group access, and Device scope.'],
              ['Invalid product or CMS', 'Use a supported product and CMS combination.'],
              [
                'Processing failure',
                'Handle the API error returned by a status, result, or export request. Correct the request or scope before retrying.'
              ]
            ]
          }
        ]
      },
      {
        id: 'aggregation',
        title: 'Field aggregation logic',
        paragraphs: [
          'Aggregation changes the shape of the returned dataset. Choose fields according to the calculation the integration needs, and keep numeric and categorical groups in separate requests where the contract requires it.'
        ],
        bullets: [
          'impressions maps to an implicit count of rows.',
          'Numeric fields support AVG and SUM.',
          'Categorical fields support COUNT and group results by the selected field.',
          'A request cannot mix numeric and categorical aggregation groups.',
          'views maps to the stored isView field.'
        ]
      },
      {
        id: 'dashboard-comparison',
        title: 'Data API and Dashboard data',
        paragraphs: [
          'The Data API returns raw detections, while Dashboard charts apply Site Group preferences and quality filters before aggregation. Two totals can therefore be valid for different processing rules.',
          'Review the maximum session time, maximum attention time, and view threshold in Profile and Site Groups before comparing raw records with Dashboard metrics. Apply the same rules to an external calculation when its totals need to match the Dashboard.'
        ],
        paragraphLinks: [{
          label: 'Profile and Site Groups',
          slug: 'profile-and-site-groups',
          section: 'site-group-preferences'
        }]
      }
    ],
    dashboardLinks: [
      { label: 'Compare data in Charts', to: '/charts' },
      { label: 'Open Reports', to: '/reports' }
    ],
    related: ['authentication-v2', 'exports-and-reports'],
    legacy: false
  },
  {
    slug: 'device-status-api',
    title: 'Device Status API',
    summary: 'Build a scheduled view of Device health across a Site Group without turning status checks into excessive polling.',
    category: 'developer',
    sources: [{ file: 'device-status-api.pdf' }],
    sections: [
      {
        id: 'prerequisites',
        title: 'Prerequisites',
        paragraphs: [
          'The endpoint returns Devices within a Site Group available to the signed-in account. Obtain the Bearer token through Authentication v2 and confirm that the account can list Devices in the intended Site Group before scheduling requests.'
        ],
        paragraphLinks: [{ label: 'Authentication v2', slug: 'authentication-v2', section: 'login' }],
        bullets: [
          'An Authentication v2 access token.',
          'A Site Group ID available to the signed-in account.',
          'Permission to list Devices in that Site Group.'
        ]
      },
      {
        id: 'retrieve',
        title: 'Step 1: Retrieve Device information',
        paragraphs: [
          'Request the Site Group Device collection with a Bearer access token. The response can contain operational identifiers and provisioning-related fields, so keep both the token and any sensitive response data out of logs.'
        ],
        tables: [
          {
            caption: 'Device status request',
            headers: ['Item', 'Value'],
            rows: [
              ['Method', 'GET'],
              ['Endpoint', 'https://api.admobilize.com/v1alpha1/projects/YOUR_SITE_GROUP_ID/devices'],
              ['Authorization', 'Bearer access token'],
              ['Content-Type', 'application/json']
            ]
          }
        ]
      },
      {
        id: 'parse',
        title: 'Step 2: Parse the response',
        paragraphs: [
          'Read each Device status from devices[].state.status, and keep devices[].state.updateTime beside it. The timestamp tells you when the service last received a state update, which is essential when deciding whether the reported state is current.'
        ],
        code: '{\n  "devices": [\n    {\n      "id": "YOUR_DEVICE_ID",\n      "displayName": "YOUR_DEVICE_NAME",\n      "state": {\n        "status": "detecting",\n        "updateTime": "STATE_UPDATE_TIME"\n      }\n    }\n  ],\n  "nextPageToken": "NEXT_PAGE_TOKEN",\n  "totalSize": 1\n}'
      },
      {
        id: 'interpret',
        title: 'Interpret Device status',
        paragraphs: [
          'Treat the state as an operational signal, not a complete diagnosis. A Device can be connected while its camera is unavailable, or be preparing models before detections begin. Understanding Device Status describes the same states from an operator’s point of view.'
        ],
        paragraphLinks: [{
          label: 'Understanding Device Status',
          slug: 'understanding-device-status',
          section: 'status-meanings'
        }],
        tables: [
          {
            caption: 'Device states',
            headers: ['Status', 'Severity', 'Meaning'],
            rows: [
              ['detecting', 'OK', 'The Device is connected and sending detections.'],
              ['downloading-models', 'OK', 'The Device is connected and preparing its models.'],
              ['inactive', 'Warning', 'The Device is connected and is not sending detections.'],
              ['no-camera', 'Warning', 'The Device is connected and does not have a usable camera input.'],
              ['offline', 'Offline', 'The Device has not connected since state.updateTime.']
            ]
          }
        ]
      },
      {
        id: 'usage',
        title: 'Usage and polling',
        paragraphs: [
          'A Device state can take several minutes to reach the API. Do not request the collection more often than every 15 minutes, and avoid interpreting a single delayed response as a new outage.',
          'Use Device notifications for alerts intended for people. Use this API for scheduled Site Group-level checks, and preserve state.updateTime with each observation so a monitoring system can distinguish a repeated status from a fresh update.'
        ],
        paragraphLinks: [{ label: 'Device notifications', slug: 'device-notifications', section: 'configure' }]
      },
      {
        id: 'pagination',
        title: 'Pagination',
        paragraphs: [
          'Set pageSize to limit each response, then pass the previous nextPageToken as pageToken on the following request. Continue until nextPageToken is empty so every Device in the Site Group is included.',
          'For example: GET /v1alpha1/projects/YOUR_SITE_GROUP_ID/devices?pageSize=PAGE_SIZE&pageToken=PAGE_TOKEN.'
        ],
        tables: [
          {
            caption: 'Pagination fields',
            headers: ['Location', 'Field', 'Purpose'],
            rows: [
              ['Request', 'pageSize', 'Maximum records requested for the page'],
              ['Request', 'pageToken', 'Token returned by the preceding page'],
              ['Response', 'nextPageToken', 'Token for the next page'],
              ['Response', 'totalSize', 'Total records when the service provides it']
            ]
          }
        ]
      },
      {
        id: 'errors',
        title: 'Handle request errors',
        paragraphs: [
          'Handle request failures separately from Device states. An authorization or rate-limit response describes the monitoring request, while a stale or missing state calls for checks against the Device and its network.'
        ],
        bullets: [
          'Refresh the access token after an authentication failure.',
          'Confirm Site Group access after a permission or not-found response.',
          'Back off after a rate-limit response.',
          'Treat a missing or stale state as unknown until the Device and network have been checked.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Profile', to: '/profile' }
    ],
    related: ['authentication-v2', 'device-notifications'],
    legacy: false
  },
  {
    slug: 'broadsign-integration',
    title: 'Broadsign integration',
    summary: 'Connect Broadsign proof-of-play records with AdMobilize Audience or Traffic measurements for the same mapped installation.',
    category: 'integrations',
    sources: [{ file: 'admobilizevision-broadsign-integration.pdf' }],
    sections: [
      {
        id: 'overview',
        title: 'About the integration',
        paragraphs: [
          'The Broadsign integration helps a media team look at what played and what AdMobilize measured around the same installation. A scheduled import joins proof-of-play records with Audience or Traffic data, associates player activity with the mapped AdMobilize Site, and makes the combined result available in Charts.'
        ],
        figures: [
          {
            src: 'assets/documentation/broadsign-data-flow.png',
            alt: 'Diagram showing Broadsign proof-of-play and AdMobilize data joining in an analytics integration',
            caption: 'The integration combines proof-of-play and AdMobilize data for analysis.'
          }
        ]
      },
      {
        id: 'getting-started',
        title: 'Getting started',
        paragraphs: [
          'Before creating the integration, make sure both platforms describe the same real-world deployment. You will need an active Broadsign player, licensed AdMobilize measurements, credentials that can read proof-of-play data, and Sites and Assets that identify where the player and Devices belong.'
        ],
        bullets: [
          'A Broadsign account with active licensed players and Proof of Play Log access.',
          'An AdMobilize account with active licenses and sensors.',
          'Broadsign credentials authorized to read proof-of-play data.',
          'Sites and Assets that represent the deployed players and Devices.'
        ],
        steps: [
          'Ask your Broadsign account contact to enable Proof of Play Log access.',
          'Collect the account credentials and proof-of-play timezone through an approved secret channel.',
          'Open Integrations in the Dashboard and select Broadsign.'
        ]
      },
      {
        id: 'accounts',
        title: 'Register an account',
        paragraphs: [
          'Use the Accounts tab to register the Broadsign credential that can read Proof of Play Log data. Enter only the fields shown by the current integration form; if a field is not present, do not invent an endpoint or copy a value from an older setup.'
        ],
        tables: [
          {
            caption: 'Broadsign account fields',
            headers: ['Field', 'Purpose'],
            rows: [
              ['Display name', 'Identifies the credential in the Dashboard.'],
              ['Username', 'Broadsign Proof of Play Log account name.'],
              ['Password', 'Secret for the Proof of Play Log account.'],
              ['Proof-of-play timezone', 'Timezone used to interpret playlog timestamps.']
            ]
          }
        ],
        steps: [
          'Select Add Account and enter the Broadsign account fields.',
          'Save the account, then use Test Account connection.',
          'Correct any failed field before creating mappings.',
          'Use the account controls to update, copy the credential reference, or remove the account.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/integrations-add-broadsign-account-form.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Add Broadsign account form with empty credential fields and proof-of-play timezone',
            caption: 'Register a credential authorized to read proof-of-play records. This example leaves the secret fields empty.'
          }
        ]
      },
      {
        id: 'assets-and-mappings',
        title: 'Register Assets and CMS mappings',
        paragraphs: [
          'The Dashboard keeps the physical structure separate from the CMS connection. An Asset belongs to a Site and can have one or more attached Devices; the CMS mapping then connects that Site or Asset context with a Broadsign player and registered account. See Understanding Sites, Assets, and Devices if the workspace structure still needs to be created.'
        ],
        paragraphLinks: [{
          label: 'Understanding Sites, Assets, and Devices',
          slug: 'sites-assets-and-devices',
          section: 'from-workspace-to-measurement'
        }],
        steps: [
          'Create or review the Site and Assets, including their Device associations.',
          'Open CMS Mappings and select Add CMS Mapping.',
          'Choose the Site and registered account, then enter Asset Name and the requested player reference.',
          'Select Create and review the saved mapping before the next import.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/integrations-create-cms-mapping-form.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Create CMS Mapping dialog with Site, credential, Asset Name, and player reference',
            caption: 'Connect the Site context and CMS player using the fields supplied by the mapping form.'
          },
          {
            src: 'assets/documentation/examples/integrations-broadsign-site-player-mappings.jpg',
            frame: { width: 2102, height: 1008, sourceWidth: 2362 },
            alt: 'Broadsign CMS Mappings list with Site names and distinct player references',
            caption: 'Review the Site and player reference for each saved mapping.'
          }
        ]
      },
      {
        id: 'processing',
        title: 'Data processing',
        paragraphs: [
          'Proof-of-play data is imported on a schedule after both the source playlogs and AdMobilize detections are available. A newly saved mapping therefore appears in analytics after a completed import, not at the moment it is created.',
          'When combined data is missing, start with the registered account and mapping. Then confirm that the Broadsign player produced proof-of-play records and that AdMobilize has measurements for the same selected period.'
        ]
      },
      {
        id: 'dashboard',
        title: 'View Broadsign analytics',
        paragraphs: [
          'After the first successful import, use Charts to explore playback context alongside the supported AdMobilize measurements. Analytics and Insights explains how to keep the Site Group, data sources, and reporting period aligned while investigating the result.'
        ],
        paragraphLinks: [{ label: 'Analytics and Insights', slug: 'analytics-and-insights', section: 'charts' }],
        steps: [
          'Open Charts after the integration completes an import.',
          'Select the Broadsign Audience or Traffic solution available to the Site Group.',
          'Choose the mapped Sites, reporting period, and required grouping.',
          'Review the proof-of-play and AdMobilize metrics together.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-broadsign-audience-cms-overview.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Broadsign Audience Charts with matched playback and audience results',
            caption: 'After an import, select the Broadsign view to explore the matched measurements.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Configure Integrations', to: '/integrations' },
      { label: 'Open Charts', to: '/charts' }
    ],
    related: ['signagelive-integration', 'analytics-and-insights'],
    legacy: false
  },
  {
    slug: 'signagelive-integration',
    title: 'Signagelive integration',
    summary: 'Connect Signagelive proof-of-play records with AdMobilize Audience measurements for the same mapped installation.',
    category: 'integrations',
    sources: [{ file: 'admobilizevision-signagelive-integration.pdf' }],
    sections: [
      {
        id: 'overview',
        title: 'About the integration',
        paragraphs: [
          'The Signagelive integration gives a media team one place to examine what played and the Audience activity measured around it. A scheduled import joins proof-of-play records with AdMobilize Audience data, associates player activity with the mapped Site, and makes the combined result available in Charts.'
        ],
        figures: [
          {
            src: 'assets/documentation/signagelive-data-flow.png',
            alt: 'Diagram showing Signagelive proof-of-play and AdMobilize data joining in an analytics integration',
            caption: 'The integration combines Signagelive proof-of-play and AdMobilize data.'
          }
        ]
      },
      {
        id: 'getting-started',
        title: 'Getting started',
        paragraphs: [
          'Prepare both sides of the integration before entering credentials. You will need an active Signagelive player, licensed AdMobilize measurements, authorization for proof-of-play access, and Dashboard Sites and Assets that match the physical deployment.'
        ],
        bullets: [
          'A Signagelive account with active licensed players.',
          'An AdMobilize account with active licenses and sensors.',
          'A Signagelive authorization token and client credentials for the integration.',
          'Sites and Assets that represent the deployed players and Devices.'
        ],
        steps: [
          'Ask Signagelive support for AdMobilize integration authorization.',
          'Collect the client credentials and authorization token through an approved secret channel.',
          'Open Integrations in the Dashboard and select Signagelive.'
        ]
      },
      {
        id: 'accounts',
        title: 'Register an account',
        paragraphs: [
          'Use the Accounts tab to register the Signagelive API credential. The client ID, client secret, and authorization token work together, so store all three only in the integration form or an approved secret store.'
        ],
        tables: [
          {
            caption: 'Signagelive account fields',
            headers: ['Field', 'Purpose'],
            rows: [
              ['Display name', 'Identifies the credential in the Dashboard.'],
              ['client_id', 'Identifies the authorized API client.'],
              ['client_secret', 'Authenticates the API client.'],
              ['auth_token', 'Authorizes access to Signagelive proof-of-play data.']
            ]
          }
        ],
        steps: [
          'Select Add Account and enter the Signagelive account fields.',
          'Save the account, then use Test Account connection.',
          'Correct any failed field before creating mappings.',
          'Use the account controls to update or remove the account.'
        ]
      },
      {
        id: 'assets-and-mappings',
        title: 'Register Assets and CMS mappings',
        paragraphs: [
          'The Dashboard maps CMS players through Sites and Assets so proof-of-play activity has the same real-world context as AdMobilize measurements. The mapping form supplies the metadata fields required by Signagelive. See Understanding Sites, Assets, and Devices when you need to prepare that structure first.'
        ],
        paragraphLinks: [{
          label: 'Understanding Sites, Assets, and Devices',
          slug: 'sites-assets-and-devices',
          section: 'from-workspace-to-measurement'
        }],
        steps: [
          'Create or review the Site and Asset that represent the screen installation.',
          'Attach the AdMobilize Device to the Asset when the deployment uses Asset-level associations.',
          'Open CMS Mappings and select Add CMS Mapping.',
          'Choose the Site, Asset when available, and registered Signagelive account.',
          'Enter the player metadata requested by the form, such as its serial or network reference.',
          'Create the mapping and review the saved values.'
        ]
      },
      {
        id: 'processing',
        title: 'Data processing',
        paragraphs: [
          'The integration imports proof-of-play data on a schedule after the source playlogs and AdMobilize detections are available. A new mapping appears in analytics after a completed import, so it may not produce an immediate chart result.',
          'When combined data is missing, check the registered account and mapping first. Then confirm that the Signagelive player produced proof-of-play records and that AdMobilize has measurements for the same selected period.'
        ]
      },
      {
        id: 'dashboard',
        title: 'View Signagelive analytics',
        paragraphs: [
          'After a successful import, Signagelive charts can place Audience metrics beside play count and group the result by Site, campaign, or media when those dimensions are present. Use Analytics and Insights to keep the reporting scope consistent while you investigate the combined data.'
        ],
        paragraphLinks: [{ label: 'Analytics and Insights', slug: 'analytics-and-insights', section: 'charts' }],
        steps: [
          'Open Charts after the integration has completed an import.',
          'Select the Signagelive solution.',
          'Choose the mapped sites and reporting period.',
          'Review views, impressions, and play count with the required grouping.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Configure Integrations', to: '/integrations' },
      { label: 'Open Charts', to: '/charts' }
    ],
    related: ['broadsign-integration', 'analytics-and-insights'],
    legacy: false
  },
  {
    slug: 'dashboard-whitelabel',
    title: 'Dashboard white-labeling',
    summary: 'Prepare a secure custom hostname and a complete set of brand assets, then validate the white-labeled experience before launch.',
    category: 'integrations',
    sources: [{ file: 'dashboard-whitelabel.pdf' }],
    sections: [
      {
        id: 'requirements',
        title: 'Requirements',
        paragraphs: [
          'A white-labeled Dashboard brings your hostname, certificate, and visual identity together, so coordinate all three with AdMobilize before changing DNS. Support will confirm the current certificate algorithms, asset dimensions, and delivery process for the deployment.',
          'Treat the certificate private key as production secret material. Transfer it only through the secure channel approved for the Site Group, never through ordinary email, tickets, or shared documents.'
        ],
        tables: [
          {
            caption: 'White-label inputs',
            headers: ['Input', 'Purpose'],
            rows: [
              ['Custom hostname', 'CNAME or agreed URL for the white-label Dashboard'],
              ['TLS certificate', 'Certificate, CA bundle, and private key for HTTPS'],
              ['Rectangular logo', 'Brand mark for the login experience'],
              ['Square logo', 'Brand mark for signed-in navigation'],
              ['Favicon', 'Browser tab icon'],
              ['Color palette', 'Login and navigation colors'],
              ['Company name', 'Customer-facing product name']
            ]
          }
        ],
        steps: [
          'Agree on the custom hostname and certificate ownership with AdMobilize.',
          'Generate the certificate material according to the current security requirements.',
          'Prepare logos, favicon, company name, and accessible brand colors.',
          'Transfer the material through the approved secure channel.',
          'Point DNS to the destination supplied by AdMobilize after support confirms readiness.'
        ]
      },
      {
        id: 'email-branding',
        title: 'Email and notification branding',
        paragraphs: [
          'Email and notification branding may have requirements beyond the Dashboard theme. Confirm the available options with AdMobilize because these channels can require a separate mail-provider account, domain verification, and approved templates.'
        ]
      },
      {
        id: 'visualization',
        title: 'Visualization',
        paragraphs: [
          'Review both the public login experience and the signed-in Dashboard before launch. Check the company name, logos, favicon, color use, readable contrast, and HTTPS certificate chain together as one customer journey.'
        ],
        figures: [
          {
            src: 'assets/documentation/whitelabel-login.png',
            alt: 'Example white-label Dashboard login page with custom red branding',
            caption: 'Verify the branded login page and password-recovery experience.'
          },
          {
            src: 'assets/documentation/whitelabel-dashboard.png',
            alt: 'Example signed-in white-label Dashboard with custom red navigation',
            caption: 'Verify the signed-in navigation, logo, colors, and content contrast.'
          }
        ]
      },
      {
        id: 'validation',
        title: 'Validate the result',
        paragraphs: [
          'Test the finished configuration from a customer’s point of view, including account recovery and email where those features are branded. This catches certificate, contrast, and identity problems before the custom hostname is shared more widely.'
        ],
        steps: [
          'Open the custom hostname over HTTPS and verify the certificate chain.',
          'Test sign-in, password recovery, and sign-out.',
          'Check the rectangular logo on the login page and square logo in navigation.',
          'Review text, controls, focus states, and charts for readable contrast.',
          'Confirm email sender and notification branding when the deployment includes them.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Profile', to: '/profile' }],
    related: ['introduction'],
    legacy: false
  },
  {
    slug: 'authentication-v1-legacy',
    title: 'Authentication v1',
    summary: 'Recognize the older two-step token flow and plan a move to the authentication method your API supports.',
    category: 'developer',
    sources: [{ file: 'authentication-v1-(legacy).pdf' }],
    sections: [
      {
        id: 'overview',
        title: 'Legacy authentication flow',
        paragraphs: [
          'Authentication v1 used two exchanges: account credentials produced an ID token, and that ID token was then exchanged for the custom token accepted by older AdMobilize APIs.',
          'This guide helps maintainers recognize existing clients. For new public API integrations, use Managing API keys. An older endpoint may still require Authentication v2, which uses an access token and refresh token.'
        ],
        paragraphLinks: [
          { label: 'Managing API keys', slug: 'managing-api-keys', section: 'create-api-key' },
          { label: 'Authentication v2', slug: 'authentication-v2', section: 'overview' }
        ],
        tables: [
          {
            caption: 'Authentication v1 token flow',
            headers: ['Step', 'Input', 'Output'],
            rows: [
              ['Get ID token', 'Account email and password', 'idToken, refreshToken, expiresIn'],
              ['Get user token', 'idToken', 'customToken'],
              ['Call an API', 'customToken as Bearer credential', 'Protected API response']
            ]
          }
        ]
      },
      {
        id: 'id-token',
        title: 'Step 1: Get an ID token',
        paragraphs: [
          'The legacy client sent account credentials as JSON and kept the returned ID token long enough to complete the second exchange. Existing clients still using this path must keep both credentials and token responses out of logs.'
        ],
        code: '{\n  "email": "YOUR_EMAIL",\n  "password": "YOUR_PASSWORD"\n}\n\n{\n  "idToken": "YOUR_ID_TOKEN",\n  "refreshToken": "YOUR_REFRESH_TOKEN",\n  "expiresIn": "EXPIRATION_INTERVAL"\n}'
      },
      {
        id: 'custom-token',
        title: 'Step 2: Get a user token',
        paragraphs: [
          'The legacy client then sent the ID token to the custom-token exchange and used the returned customToken as the Bearer credential for supported APIs.'
        ],
        code: '{\n  "idToken": "YOUR_ID_TOKEN"\n}\n\n{\n  "customToken": "YOUR_CUSTOM_TOKEN"\n}'
      },
      {
        id: 'migration',
        title: 'Move from v1 to v2 on legacy endpoints',
        steps: [
          'Replace the legacy credential exchange with the Authentication v2 login endpoint.',
          'Remove the second ID-token-to-custom-token exchange.',
          'Send the v2 accessToken as the Bearer credential to APIs that support Authentication v2.',
          'Store the v2 refreshToken and replace it after each successful refresh.',
          'Test token expiry, refresh failure, and API authorization before removing the legacy path.'
        ],
        paragraphs: [
          'Confirm that every target API accepts Authentication v2 before switching a production integration. Test the complete login, refresh, expiry, and authorization path in a safe environment, and contact AdMobilize support when an older API still requires customToken.'
        ],
        paragraphLinks: [{ label: 'Authentication v2', slug: 'authentication-v2', section: 'overview' }]
      }
    ],
    dashboardLinks: [{ label: 'Open Profile', to: '/profile' }],
    related: ['managing-api-keys', 'authentication-v2'],
    legacy: true,
    legacyNotice: {
      title: 'Legacy',
      paragraphs: ['This guide describes a legacy authentication flow.'],
      replacement: {
        before: 'For new public API integrations, create and manage ',
        after: ' in the Dashboard.',
        label: 'API keys',
        slug: 'managing-api-keys'
      }
    }
  }
];
